import { useLogSignalR } from 'store/logger';

import { logRed } from 'utils/debugger';

import { HUB_URL, MethodName } from '../const';
import { useSignalRConnection } from './useSignalRConnection';

/**
 * Create and initialize a connection to a signalR hub
 *
 * Should only be used in the context of a provider by design
 */
export function useHubConnection(url: HUB_URL) {
  const { connection, isConnected, status } = useSignalRConnection(url);

  const { logSignalRReceive, logSignalRInvoke, logSignalRInit } = useLogSignalR(url);

  /**
   * Todo - remove callback
   * Todo - type a general response of invoking if there is any?
   * Note: we could not add many listeners to the same connection currently because unsubscribe will remove all
   */
  async function invoke<T = any>(methodName: MethodName, ...args: any[]): Promise<T> {
    logSignalRInvoke(`signalR:${url} ⬆️⬆️⬆️ invoke: ${methodName}`, args);
    if (!connection) {
      const message = `signalR:${url} 🚫🚫🚫connection not initialized: ${methodName}`;
      logRed(message);
      throw new Error(message);
    }

    return await connection.invoke<T>(methodName, ...args);
  }

  function subscribe(methodName: MethodName, callback: (...args: any[]) => void) {
    logSignalRInit(`signalR:${url} 📩📩📩 subscribed: ${methodName}`);
    if (!connection) {
      const message = `signalR:${url} 🚫🚫🚫connection not initialized: ${methodName}`;
      logRed(message);
      throw new Error(message);
    }

    connection.on(methodName, (...args: any[]) => {
      // todo - find out why this is here and document reason for this
      if (args[0]?.payload && typeof args[0]?.payload === 'string') {
        args[0].payload = JSON.parse(args[0].payload);
      }

      logSignalRReceive(`signalR:${url} 📩📩📩 received: ${methodName}:`, ...args);

      try {
        callback(...args);
      } catch (e) {
        logRed(`signalR:${url} error - calling callback: ${methodName}:`, e);
      }
    });
  }

  const unsubscribe = (methodName: MethodName, callback?: (...args: any[]) => void) => {
    logSignalRInit(`signalR:${url} 👋👋👋 unsubscribed: ${methodName}`);
    if (!connection) {
      const message = `signalR:${url} 🚫🚫🚫connection not initialized: ${methodName}`;
      logRed(message);
      throw new Error(message);
    }

    if (callback) {
      // this will unsubscribe only the specific listener if the reference is the same as in on invocation
      connection.off(methodName, callback);
    } else {
      // this will unsubscribe all listeners to this method name
      connection.off(methodName);
    }
  };

  return {
    connection, // todo - shouldn't be return
    isConnected,
    status,
    invoke,
    subscribe,
    unsubscribe,
  };
}
